import { classNames } from "primereact/utils";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import { IMyPlanViewModel } from "../models/MyPlanViewModel";
import styles from "./PlanCard.module.scss";

interface IPlanCardProps {
  plan: IMyPlanViewModel;
}

const formatCurrency = (symbol: string): string => {
  const symbolMap: Record<string, string> = {
    EUR: "€",
    BGN: "лв",
  };
  return symbolMap[symbol] || symbol;
};

export const PlanCard = ({ plan }: IPlanCardProps) => {
  const isUnlimited = plan.remainingVisits === null;
  // No visits left means the plan is "completed" (приключен) rather than just
  // "expired" (изтекъл). Unlimited plans (null) can only expire by date.
  const isExhausted =
    plan.remainingVisits !== null && plan.remainingVisits <= 0;
  const usedVisits =
    plan.initialVisits && plan.remainingVisits !== null
      ? plan.initialVisits - plan.remainingVisits
      : 0;
  const progressPercentage =
    plan.initialVisits && plan.remainingVisits !== null
      ? (usedVisits / plan.initialVisits) * 100
      : 0;
  const currencySymbol = formatCurrency(plan.currencySymbol);

  const cardClass = classNames(styles.card, {
    [styles.card_expired]: !plan.isActive,
    [styles.card_family]: plan.isFamily && plan.isActive,
  });

  return (
    <div className={cardClass}>
      <div className={styles.gradient_overlay} />

      <div className={styles.header}>
        <div className={styles.header_left}>
          <div className={styles.plan_type_badge}>
            {plan.isFamily ? "Семеен план" : "Индивидуален план"}
          </div>
          <div className={styles.plan_name}>{plan.planName}</div>
          <div className={styles.plan_owner}>{plan.ownerName}</div>
        </div>
        <div className={styles.header_right}>
          <span
            className={classNames(styles.status_badge, {
              [styles.status_badge_active]: plan.isActive,
              [styles.status_badge_expired]: !plan.isActive,
            })}
          >
            {plan.isActive
              ? "Активен"
              : isExhausted
              ? "Приключен"
              : "Изтекъл"}
          </span>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.info_row}>
          <div>
            <div className={styles.info_label}>Валиден от</div>
            <div className={styles.info_value}>
              {dateUtility.formatDate(plan.effectiveFrom, "dd.MM.yyyy")}
            </div>
          </div>
          <div>
            <div className={styles.info_label}>Валиден до</div>
            <div className={styles.info_value}>
              {dateUtility.formatDate(plan.activeUntil, "dd.MM.yyyy")}
            </div>
          </div>
          <div>
            <div className={styles.info_label}>Платено</div>
            <div className={styles.info_value}>
              {currencySymbol === "€"
                ? `${currencySymbol}${plan.pricePaid.toFixed(2)}`
                : `${plan.pricePaid.toFixed(2)} ${currencySymbol}`}
            </div>
          </div>
        </div>

        {isUnlimited ? (
          <div className={styles.info_row}>
            <div>
              <div className={styles.info_label}>Посещения</div>
              <div className={styles.info_value}>
                <span className={styles.unlimited_badge}>Неограничени</span>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.visits_progress}>
            <div className={styles.visits_label}>
              <span className={styles.visits_text}>Използвани посещения</span>
              <span className={styles.visits_count}>
                {usedVisits} / {plan.initialVisits}
              </span>
            </div>
            <div className={styles.progress_bar_container}>
              <div
                className={styles.progress_bar_fill}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const PlanCardSkeleton = () => {
  return (
    <div className={styles.card}>
      <div className={styles.gradient_overlay} />

      <div className={styles.header}>
        <div className={styles.header_left}>
          <div className={styles.skeleton_badge} />
          <div className={styles.skeleton_title} />
          <div className={styles.skeleton_subtitle} />
        </div>
        <div className={styles.header_right}>
          <div className={styles.skeleton_status} />
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.info_row}>
          <div>
            <div className={styles.skeleton_label} />
            <div className={styles.skeleton_value} />
          </div>
          <div>
            <div className={styles.skeleton_label} />
            <div className={styles.skeleton_value} />
          </div>
          <div>
            <div className={styles.skeleton_label} />
            <div className={styles.skeleton_value} />
          </div>
        </div>

        <div className={styles.visits_progress}>
          <div className={styles.visits_label}>
            <div className={styles.skeleton_visits_text} />
            <div className={styles.skeleton_visits_count} />
          </div>
          <div className={styles.skeleton_progress_bar} />
        </div>
      </div>
    </div>
  );
};
